import {
  PersistedFormGroup,
  PersistedFormArray,
  PersistedArray,
} from './persisted-form-group';
import type {
  PersistedFormGroupOptions,
  PersistedFormArrayOptions,
  PersistedArrayOptions,
} from './persisted-form-group';
import type { SchemaFormControl } from '@apexdesigner/schema-forms';
import { NpmPackage } from './npm-package';
export { NpmPackage };
import { npmPackageSchema } from '@schemas/business-objects/npm-package';
import { PackageDocFormArray } from './package-doc-form-group';
import { PackageVersionFormArray } from './package-version-form-group';
import { RegistryTokenFormArray } from './registry-token-form-group';
import type { PublishedPackageInput } from '../interface-definitions/index';
import type { PackageCategory } from '../base-types';
import type { PackageEntitlement } from './package-entitlement';
import type { SystemTaskExecution } from './system-task-execution';

export class NpmPackageFormGroup extends PersistedFormGroup {
  declare controls: {
    id: SchemaFormControl;
    name: SchemaFormControl;
    publishRepository: SchemaFormControl;
    publishWorkflow: SchemaFormControl;
    publishEnvironment: SchemaFormControl;
    description: SchemaFormControl;
    access: SchemaFormControl;
    deprecatedMessage: SchemaFormControl;
    upstreamSyncedAt: SchemaFormControl;
    distTagsJson: SchemaFormControl;
    category: SchemaFormControl;
    categoryOverride: SchemaFormControl;
    latestVersion: SchemaFormControl;
    latestPublishedAt: SchemaFormControl;
    latestNewsSummary: SchemaFormControl;
    hasDocs: SchemaFormControl;
    hasNews: SchemaFormControl;
    registryTokens: RegistryTokenFormArray;
    versions: PackageVersionFormArray;
    packageDocs: PackageDocFormArray;
  };

  declare value: Partial<NpmPackage>;

  constructor(
    data?: Record<string, any> | null,
    options?: PersistedFormGroupOptions,
  ) {
    super(npmPackageSchema, NpmPackage, data, options);
  }

  get object(): NpmPackage {
    return new NpmPackage(this.getRawValue());
  }

  protected override createControl(name: string) {
    switch (name) {
      case 'registryTokens':
        return new RegistryTokenFormArray();
      case 'versions':
        return new PackageVersionFormArray();
      case 'packageDocs':
        return new PackageDocFormArray();
      default:
        return undefined;
    }
  }

  static async find(filter?: any): Promise<NpmPackage[]> {
    return NpmPackage.find(filter);
  }

  static async findOne(filter?: any): Promise<NpmPackage | null> {
    return NpmPackage.findOne(filter);
  }

  static async findById(id: number, filter?: any): Promise<NpmPackage> {
    return NpmPackage.findById(id, filter);
  }

  static async create(data: Partial<NpmPackage>): Promise<NpmPackage> {
    return NpmPackage.create(data as any);
  }

  static async updateById(
    id: number,
    updates: Partial<NpmPackage>,
  ): Promise<NpmPackage> {
    return NpmPackage.updateById(id, updates as any);
  }

  static async deleteById(id: number): Promise<boolean> {
    return NpmPackage.deleteById(id);
  }

  static async visiblePackage(filter?: any): Promise<NpmPackageFormGroup> {
    const result = await NpmPackage.visiblePackage(filter);
    const fg = new NpmPackageFormGroup();
    fg._populate(result);
    return fg;
  }

  static async visiblePackages(filter?: any): Promise<NpmPackageFormGroup[]> {
    const result = await NpmPackage.visiblePackages(filter);
    return (result || []).map((item: any) => {
      const fg = new NpmPackageFormGroup();
      fg._populate(item);
      return fg;
    });
  }

  static async published(
    body: PublishedPackageInput,
    xNewsDraftToken: string,
  ): Promise<{ queued: boolean; name: string; version: string }> {
    return NpmPackage.published(body, xNewsDraftToken);
  }

  static async pullVersions(
    name: string,
    versions: string[],
  ): Promise<
    { version: string; added?: boolean; unpacked?: boolean; error?: string }[]
  > {
    return NpmPackage.pullVersions(name, versions);
  }

  async setCategoryOverride(
    category?: PackageCategory | null,
  ): Promise<{ id: number; category: string; categoryOverride?: string }> {
    const instance = new NpmPackage(this.getRawValue());
    return instance.setCategoryOverride(category);
  }

  static async syncCatalog(packages?: string[]): Promise<any> {
    return NpmPackage.syncCatalog(packages);
  }
}

export class NpmPackageFormArray extends PersistedFormArray {
  declare controls: NpmPackageFormGroup[];

  readonly entityName = 'NpmPackage' as const;

  constructor(options?: PersistedFormArrayOptions) {
    super(npmPackageSchema, NpmPackage, options);
  }

  protected override createItemGroup() {
    return new NpmPackageFormGroup();
  }

  get array(): NpmPackage[] {
    return this.controls.map((group: NpmPackageFormGroup) => group.object);
  }
}

export class NpmPackagePersistedArray extends PersistedArray<NpmPackage> {
  readonly entityName = 'NpmPackage' as const;

  constructor(options?: PersistedArrayOptions) {
    super(NpmPackage, options);
  }
}
